// Types
import React from "react";
import { Driver } from "@/types/supabase";

// Hooks
import { useDriverImageUpload } from "@/hooks/useDriverImageUpload";

// Components
import { Button } from "@/components/atoms/button";

// Icons
import { Upload, Loader2 } from "lucide-react";

interface DriverAvatarProps {
  driver: Driver;
  setDriver: (driver: Driver) => void;
  isEditing: boolean;
  onSaveFormData?: () => void;
  onImageUploadStateChange?: (isProcessing: boolean) => void;
  onImageMethodsReady?: (methods: {
    resetImageSelection: () => void;
    hasSelectedImage: () => boolean;
    uploadImage?: () => Promise<void>;
  }) => void;
}

export function DriverAvatar({
  driver,
  setDriver,
  isEditing,
  onSaveFormData,
  onImageUploadStateChange,
  onImageMethodsReady,
}: DriverAvatarProps) {
  const {
    displayImageSrc,
    isUploadingImage,
    isProcessingImage,
    selectedImageFile,
    fileInputRef,
    handleFileChange,
    resetImageSelection,
    uploadImage,
  } = useDriverImageUpload({
    driver,
    setDriver,
    isEditing,
    onSaveFormData,
  });

  // Notify parent component about image upload state changes
  React.useEffect(() => {
    onImageUploadStateChange?.(isProcessingImage || isUploadingImage);
  }, [isProcessingImage, isUploadingImage, onImageUploadStateChange]);

  // Expose image methods to parent component
  React.useEffect(() => {
    if (onImageMethodsReady) {
      onImageMethodsReady({
        resetImageSelection,
        hasSelectedImage: () => !!selectedImageFile,
        uploadImage,
      });
    }
  }, [
    selectedImageFile,
    uploadImage,
    resetImageSelection,
    onImageMethodsReady,
  ]);

  return (
    <div className="flex flex-col items-center lg:items-start gap-4 min-w-[140px]">
      <img
        key={displayImageSrc}
        src={displayImageSrc}
        alt="Avatar"
        className="h-28 w-28 rounded-full object-cover border"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,image/tiff,image/svg+xml"
        onChange={handleFileChange}
        className="hidden"
        multiple={false}
        aria-label="Seleccionar imagen de perfil"
        title="Seleccionar imagen de perfil del conductor"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={`mt-2 transition-all duration-200 ${
          !isEditing || isProcessingImage
            ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
        }`}
        disabled={!isEditing || isProcessingImage}
        onClick={() => fileInputRef.current?.click()}
      >
        {isProcessingImage ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Procesando...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4 mr-2" />
            Cambiar foto
          </>
        )}
      </Button>

      {isEditing && !isProcessingImage && (
        <div className="text-xs text-gray-600 text-center mt-1 max-w-[120px]">
          {selectedImageFile ? (
            <>
              <span className="text-blue-600 font-medium">Imagen lista</span>
              <br />
              <span>Se subirá al guardar</span>
            </>
          ) : (
            <>
              JPG, PNG, WebP hasta 5MB
              <br />
              Mínimo 50x50px
            </>
          )}
        </div>
      )}
    </div>
  );
}
